export interface SeveranceFormValues {
  monthlySalary: number;
  annualBonus: number;
  workingYears: number;
  workingMonths: number;
}

export interface SeveranceResult {
  averageDailyWage: number;
  legalSeverance: number;
  taxAmount: number;
  netSeverance: number;
  totalWorkingDays: number;
  monthlyEquivalent: number;
}

const MONTHS_PER_YEAR = 12;
const MANWON_TO_WON = 10_000;

export function calculateSeverance(values: SeveranceFormValues): SeveranceResult {
  const monthlySalaryWon = values.monthlySalary * MANWON_TO_WON;
  const annualBonusWon = values.annualBonus * MANWON_TO_WON;
  const recentThreeMonthIncome = monthlySalaryWon * 3 + (annualBonusWon * 3) / MONTHS_PER_YEAR;
  const averageDailyWage = recentThreeMonthIncome / 90;
  const totalWorkingDays = values.workingYears * 365 + values.workingMonths * 30;
  const legalSeverance = averageDailyWage * 30 * (totalWorkingDays / 365);
  const taxAmount = estimateRetirementIncomeTax(legalSeverance);
  const netSeverance = Math.max(0, legalSeverance - taxAmount);
  const totalWorkingMonths = Math.max(1, values.workingYears * MONTHS_PER_YEAR + values.workingMonths);
  const monthlyEquivalent = legalSeverance / totalWorkingMonths;

  return {
    averageDailyWage,
    legalSeverance,
    taxAmount,
    netSeverance,
    totalWorkingDays,
    monthlyEquivalent,
  };
}

function estimateRetirementIncomeTax(legalSeverance: number): number {
  if (legalSeverance < 30_000_000) {
    return legalSeverance * 0.06;
  }

  if (legalSeverance <= 80_000_000) {
    return Math.max(0, legalSeverance * 0.15 - 1_740_000);
  }

  return Math.max(0, legalSeverance * 0.24 - 8_940_000);
}

export function getSeveranceCallout(result: SeveranceResult) {
  if (result.legalSeverance < 10_000_000) {
    return {
      title: 'IRP 계좌로 이전하면 세금 이연이 가능합니다',
      body: '작은 금액의 퇴직금도 바로 수령하기보다 IRP로 옮기면 과세 시점을 뒤로 미뤄 운용 기간을 확보할 수 있습니다.',
      cta: { label: 'IRP·ISA 세제혜택 계산기로 절세 효과 확인', href: '/tax-benefit' },
    };
  }

  if (result.legalSeverance <= 50_000_000) {
    return {
      title: 'IRP 이전 후 연금 수령 시 세율을 30~40% 줄일 수 있습니다',
      body: '퇴직금을 한 번에 받기보다 IRP로 이전한 뒤 연금으로 수령하면 퇴직소득세 부담을 낮추고 운용 수익도 기대할 수 있습니다.',
      cta: { label: 'IRP·ISA 세제혜택 계산기로 절세 효과 확인', href: '/tax-benefit' },
    };
  }

  return {
    title: '퇴직소득세 절감을 위해 IRP 이전이 특히 중요합니다',
    body: '퇴직금 규모가 커질수록 세금 차이도 커집니다. 퇴직 직후 사용할 계획이 없다면 IRP 이전 전략을 우선 검토해 보세요.',
    cta: { label: 'IRP·ISA 세제혜택 계산기로 절세 효과 확인', href: '/tax-benefit' },
  };
}
